import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STEPS } from "@/components/valuation/questions";
import StepView from "@/components/valuation/StepView";
import Calculating from "@/components/valuation/Calculating";
import PreviewGate from "@/components/valuation/PreviewGate";

const SPLIT_IMG =
  "https://images.unsplash.com/photo-1635151227785-429f420c6b9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGVsZWdhbnQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NDYwODE0MHww&ixlib=rb-4.1.0&q=85";

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
  };
}

export default function Valuation() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("questions"); // questions | calculating | gate
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const visibleSteps = STEPS.filter((s) => !s.showIf || s.showIf(answers));
  const safeIndex = Math.min(stepIndex, visibleSteps.length - 1);
  const step = visibleSteps[safeIndex];
  const progress =
    phase === "questions"
      ? ((safeIndex + 1) / visibleSteps.length) * 100
      : 100;

  const setAnswer = (val) => setAnswers((a) => ({ ...a, [step.key]: val }));

  const finishQuestions = useCallback(async () => {
    setPhase("calculating");
    try {
      const { data } = await api.post("/valuation/preview", { answers });
      setPreview(data);
    } catch (e) {
      toast.error("Could not calculate. Please try again.");
      setPhase("questions");
    }
  }, [answers]);

  const onNext = () => {
    if (safeIndex < visibleSteps.length - 1) {
      setStepIndex(safeIndex + 1);
    } else {
      finishQuestions();
    }
  };
  const onBack = () => setStepIndex(Math.max(0, safeIndex - 1));

  const submitLead = async (contact) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/valuation/submit", {
        answers,
        contact,
        ...getUTM(),
      });
      toast.success("Your report is ready and on its way to your inbox.");
      navigate("/result", {
        state: {
          report: data.report,
          firstName: data.contact?.first_name || contact.first_name,
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  // Split-screen layout
  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-background">
      {/* Left brand pane */}
      <div className="hidden lg:flex lg:col-span-4 relative overflow-hidden bg-primary">
        <img src={SPLIT_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative z-[2] flex flex-col justify-between p-10 text-primary-foreground w-full">
          <Link to="/" className="flex flex-col leading-none" data-testid="valuation-logo">
            <span className="font-serif text-3xl tracking-tight">Salons For Sale</span>
            <span className="eyebrow text-[0.55rem] text-primary-foreground/60 mt-1">
              Salon Valuations
            </span>
          </Link>
          <div>
            <h2 className="font-serif font-light text-4xl leading-[1.0] tracking-tight mb-8">
              A confidential,
              <br />
              indicative valuation
              <br />
              in about two minutes.
            </h2>
            <div className="h-[3px] bg-white/15 w-full overflow-hidden">
              <motion.div
                className="h-full bg-brand"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <div className="eyebrow text-primary-foreground/60 mt-3">
              {Math.round(progress)}% complete
            </div>
          </div>
        </div>
      </div>

      {/* Right content pane */}
      <div className="lg:col-span-8 relative flex flex-col">
        <header className="flex items-center justify-between px-6 md:px-12 h-20 lg:hidden border-b border-border">
          <Link to="/" className="flex items-center"><img src="/salons-for-sale.png" alt="Salons For Sale" className="h-10 w-auto" /></Link>
          <div className="flex-1 mx-4 h-[3px] bg-border overflow-hidden">
            <motion.div className="h-full bg-brand" animate={{ width: `${progress}%` }} />
          </div>
        </header>

        <button
          data-testid="valuation-close"
          onClick={() => navigate("/")}
          className="absolute top-6 right-6 z-10 hidden lg:grid place-items-center w-11 h-11 rounded-full border border-border hover:border-brand transition-colors"
        >
          <X size={18} />
        </button>

        <div className="flex-1 flex items-center px-6 md:px-16 py-16 md:py-20">
          <AnimatePresence mode="wait">
            {phase === "questions" && (
              <StepView
                key={step.id}
                step={step}
                value={answers[step.key]}
                onChange={setAnswer}
                onNext={onNext}
                onBack={onBack}
                index={safeIndex}
                total={visibleSteps.length}
              />
            )}
            {phase === "calculating" && (
              <Calculating key="calc" onDone={() => setPhase("gate")} />
            )}
            {phase === "gate" && (
              <PreviewGate
                key="gate"
                preview={preview}
                onSubmit={submitLead}
                submitting={submitting}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
